﻿import * as dagreD3 from "dagre-d3";

import {
    createEdgeLabel, attachLinks, createInfoHeader, createInfo, createNodeDescriptions,
    createNodeHeaderText, createNodeBoxText, formatList, formatDate, loadImage
} from "./familyTreeLinkPageHelper";

export default class FamilyTreeLink {
    constructor() {
        this.baseUrl = $("body").data("base-url");
        this.render = new dagreD3.render();
        this.fetchTreeArgument = {};
        this.nodeDisplayOptions = [];
        this.parentToChildren = new Map();
        this.childToParents = new Map();
        this.fullscreen = false;
        this.treeJson = {};
        this.isTD = false;
        this.stats = {};

        const self = this;
        
        // for non TD views
        this.render.shapes().treeNode = function (parent, bbox, node) {
            const { childToParents, parentToChildren } = self;

            let rectHeight = 25;
            const secondRectHeight = 35;
            const totalHeight = rectHeight + secondRectHeight;
            rectHeight = node.Type !== "F" ? rectHeight : totalHeight;

            const firstRectY = -totalHeight / 2;
            const secondRectY = firstRectY + rectHeight;
            const rectWidth = 175;
            const rectX = -rectWidth / 2;
            const baseFontSize = 12;

            let directParents = (childToParents.get(node.Id) || [])
                .map(parentId => ({ id: parentId, name: g.node(parentId).Text }));
            let directChildren = (parentToChildren.get(node.Id) || [])
                .map(childId => ({ id: childId, name: g.node(childId).Text }));

            const shapeSvg = parent.insert("g", ":first-child");

            const d1 = `
              M ${rectX}, ${firstRectY + rectHeight} 
              L ${rectX}, ${firstRectY} 
              Q ${rectX}, ${firstRectY} ${rectX}, ${firstRectY} 
              L ${rectX + rectWidth}, ${firstRectY} 
              Q ${rectX + rectWidth}, ${firstRectY} ${rectX + rectWidth}, ${firstRectY} 
              L ${rectX + rectWidth}, ${firstRectY + rectHeight} 
              Z
            `;

            const path = shapeSvg.append("path")
                .attr("d", d1)
                .attr("fill", "white")
                .attr("stroke", "black")
                .attr("stroke-width", "1");

            if (node.Type === "C" || node.Type === "T")
                path.attr("class", node.Active ? "activeNode" : "inactiveNode");


            const delimiter = node.Type === "F" ? " " : "/";
            const maxWidth = node.Type === "F" ? rectWidth + 5 : rectWidth - 15;
            const headerText = createNodeHeaderText(node.Text, maxWidth, delimiter);
            const xShift = rectX + 0.5 * baseFontSize;
            const yShift = headerText[1] == "" ? firstRectY + (rectHeight + baseFontSize) / 2 : firstRectY + baseFontSize - 1.5;

            const headerTextElement = shapeSvg.append("text")
                .attr("x", xShift)
                .attr("y", yShift)
                .attr("font-weight", "bold")
                .attr("font-size", baseFontSize)
                .attr("font-family", "Arial, sans-serif")
                .attr("xml:space", "preserve");

            $.each(headerText, function (i, text) {
                if (text === "") return false;
                headerTextElement.append("tspan")
                    .attr("x", xShift)
                    .attr("dy", i === 0 ? "0em" : "1em")
                    .text(i === 0 ? text : "\t".repeat(4) + text);
            })


            if (node.Type !== "F") {
                const d2 = `
                    M ${rectX}, ${secondRectY} 
                    L ${rectX + rectWidth}, ${secondRectY} 
                    L ${rectX + rectWidth}, ${secondRectY + secondRectHeight} 
                    Q ${rectX + rectWidth}, ${secondRectY + secondRectHeight} ${rectX + rectWidth}, ${secondRectY + secondRectHeight} 
                    L ${rectX}, ${secondRectY + secondRectHeight} 
                    Q ${rectX}, ${secondRectY + secondRectHeight} ${rectX}, ${secondRectY + secondRectHeight} 
                    L ${rectX}, ${secondRectY} 
                    Z
                `;

                shapeSvg.append("path")
                    .attr("d", d2)
                    .attr("fill", "white")
                    .attr("stroke", "black")
                    .attr("stroke-width", "1");


                attachLinks(shapeSvg, node, directParents, directChildren, rectWidth, firstRectY);
                const desText = createNodeDescriptions(node);

                const desTextElement = shapeSvg.append("text")
                    .attr("x", xShift)
                    .attr("y", secondRectY + baseFontSize + 3)
                    .attr("xml:space", "preserve");

                $.each(desText, function (i, text) {
                    desTextElement.append("tspan")
                        .attr("x", xShift)
                        .attr("dy", i === 0 ? "0em" : "1.2em")
                        .attr("font-size", baseFontSize)
                        .attr("font-family", "Arial, sans-serif")
                        .text(text);
                })
            }

            node.intersect = function (point) {
                const safeNode = {
                    x: node.x,
                    y: node.y,
                    width: rectWidth,
                    height: totalHeight,
                };

                return dagreD3.intersect.rect(safeNode, point);
            };

            return shapeSvg;
        };


        // TD only shape
        this.render.shapes().TDTreeNode = function (parent, bbox, node) {
            let baseFontSize = 12;
            const radius = 15;

            const shapeSvg = parent.insert("g", ":first-child");
            shapeSvg.append("circle")
                .attr("r", radius)
                .style("fill", "black");

            desText = createNodeBoxText(node, 250, baseFontSize, node.TDProperties);
            const desTextElement = shapeSvg.append("text")
                .attr("text-anchor", "middle")
                .attr("x", 0)
                .attr("y", radius * 2)
                .attr("xml:space", "preserve");

            $.each(desText, function (i, text) {
                desTextElement.append("tspan")
                    .attr("x", 0)
                    .attr("dy", i === 0 ? "0em" : "1.2em")
                    .attr("font-weight", i === 0 ? "bold" : "")
                    .attr("font-size", baseFontSize)
                    .attr("font-family", "Arial, sans-serif")
                    .text(text);
            });

            node.intersect = function (point) {
                const safeNode = {
                    x: node.x,
                    y: node.y,
                    width: (radius + 10) * 2
                };

                return dagreD3.intersect.rect(safeNode, point);
            };

            return shapeSvg;
        };
    }


    async renderTree(data, refreshData = false) {
        this.fetchTreeArgument = data;

        try {
            if ((Object.keys(this.treeJson).length === 0) || refreshData) {
                this.treeJson = await this.fetchTreeData();
            }
            this.isTD = this.treeJson["Area"] === 1;
            if (!this.isTD) {
                let { properties, _ } = await this.fetchDisplayOptions();
                this.nodeDisplayOptions = properties;
            }

            this.renderTreeInternal();
            this.attachEventListeners();
        } catch (error) {
            console.error(error);
            pageHelper.showErrors(`Error rendering family tree, please report this as a bug.`);
        }
    };


    centerGraph() {
        const d3svg = d3.select("#familyTreeSvg");
        const svgContainer = $("#d3-tree");
        const svgHeader = document.getElementById("svgHeader");
        const padding = 15;

        if (!g || !g.graph) return;

        const graph = g.graph();
        const graphWidth = graph.width || 0;
        const graphHeight = graph.height || 0;

        if (!graphWidth || !graphHeight) return;

        const headerHeight = svgHeader ? svgHeader.getBBox().height : 0;
        const availableHeight = svgContainer.height() - headerHeight - 2 * padding;

        const scaleX = svgContainer.width() / graphWidth;
        const scaleY = availableHeight / graphHeight;
        const zoomScale = Math.min(scaleX, scaleY, 1);

        const x0 = (svgContainer.width() - graphWidth * zoomScale) / 2;
        const y0 = (availableHeight - graphHeight * zoomScale) / 2 + headerHeight + padding;

        d3svg.transition()
            .duration(1500)
            .call(zoom.transform, d3.zoomIdentity.translate(x0, y0).scale(zoomScale));
    }


    centerAroundNode(node) {
        if (!node) {
            console.error(node);
            return;
        }

        const d3svg = d3.select("#familyTreeSvg");
        const svgContainer = $("#d3-tree");
        const ouputCanvas = $(".output");
        const selectedNode = $("#" + node.Id);

        if (!selectedNode.length) return;

        const currentTransform = d3.zoomTransform(d3svg.node());
        const zoomedScale = 1;

        if (selectedNode[0].getBBox) {
            const bbox = selectedNode[0].getBBox();
            const selectedWidth = bbox.width * zoomedScale;
            const selectedHeight = bbox.height * zoomedScale;

            const relativeX = (selectedNode.offset().left - ouputCanvas.offset().left) / currentTransform.k * zoomedScale;
            const relativeY = (selectedNode.offset().top - ouputCanvas.offset().top) / currentTransform.k * zoomedScale;

            const graphWidth = g.graph().width * zoomedScale;
            const graphHeight = g.graph().height * zoomedScale;

            const xtrans = (svgContainer.width() - graphWidth) / 2 + (graphWidth / 2 - relativeX) - selectedWidth / 2;
            const ytrans = (svgContainer.height() - graphHeight) / 2 + (graphHeight / 2 - relativeY) - selectedHeight / 2;

            d3svg.transition().duration(1500).call(zoom.transform, d3.zoomIdentity.translate(xtrans, ytrans).scale(zoomedScale));
        }
    }


    applicationsGridDataBound() {

        const grid = $("#familyTreeInventionApplicationGrid").data("kendoGrid");
        const dataSource = grid.dataSource;
        const data = dataSource.view();

        if (data.length === 0) {
            console.warn("No data available yet.");
            return;
        }

        const sort = dataSource.sort() || [];

        if (sort.length > 0) {
            const query = new kendo.data.Query(data);
            try {
                const sortedData = query.sort(sort).data;
                this.applicationAppIds = sortedData.map(e => e.AppId);
            } catch (error) {
                console.error("Sorting error:", error, "Sort value:", sort);
            }
        }
    }


    getInventionApplicationGrid() {
        const countryApplicationSearchParam = () => {
            var InvId = 0;
            var foundNode = this.treeJson["Nodes"].find(x => x.Type === "I");
            if (foundNode) {
                InvId = foundNode.Id;
            }
            return {
                CountryApplicationInvId: InvId,
                CountryApplicationCaseNumber: "",
                CountryApplicationCountry: "",
                CountryApplicationSubCase: "",
                CountryApplicationCaseType: "",
                CountryApplicationStatus: "",
                CountryApplicationAppNumber: ""
            };
        };

        $("#familyTreeInventionApplicationGrid").kendoGrid({
            dataSource: {
                transport: {
                    read: function (options) {
                        $.ajax({
                            url: "/Patent/CountryApplication/InventionCountryApplicationGridRead",
                            dataType: "json",
                            type: "POST",
                            contentType: "application/x-www-form-urlencoded",
                            data: countryApplicationSearchParam(),
                            cache: true,
                            success: function (response) {
                                if (response.Data && Array.isArray(response.Data)) {
                                    response.Data.forEach(item => {
                                        if (item.IssDate)
                                            item.IssDate = formatDate(item.IssDate);

                                        if (item.FilDate) 
                                            item.FilDate = formatDate(item.FilDate);
                                    });
                                }

                                options.success(response);
                            },
                            error: function (e) {
                                options.error(e);
                                // pageHelper.showErrors(e);
                            }
                        });
                    }
                },
                serverPaging: false,
                serverSorting: false,
                serverFiltering: false,
                schema: {
                    data: "Data",
                    total: "Total"
                },
                sort: { field: "Country", dir: "asc" },
                error: function (e) {
                    pageHelper.showErrors(e);
                }
            },
            autoBind: false,
            columns: [
                {
                    field: "CaseNumber",
                    title: "Case Number",
                    attributes: { class: "d-none d-md-table-cell name-field" },
                    headerAttributes: { class: "d-none d-md-table-cell" }
                },
                {
                    field: "Country",
                    title: "Country",
                },
                {
                    field: "SubCase",
                    title: "Sub Case",
                    attributes: { class: "d-none d-md-table-cell name-field" },
                    headerAttributes: { class: "d-none d-md-table-cell" }
                },
                {
                    field: "CaseType",
                    title: "Case Type",
                    attributes: { class: "d-none d-md-table-cell name-field" },
                    headerAttributes: { class: "d-none d-md-table-cell" }
                },
                {
                    field: "ApplicationStatus",
                    title: "Status",
                },
                {
                    field: "AppNumber",
                    title: "Application No.",
                },
                {
                    field: "FilDate",
                    title: "Filling Date",
                    attributes: { class: "d-none d-md-table-cell name-field" },
                    headerAttributes: { class: "d-none d-md-table-cell" }
                },
                {
                    field: "PatNumber",
                    title: "Patent Number",
                    attributes: { class: "d-none d-lg-table-cell name-field" },
                    headerAttributes: { class: "d-none d-lg-table-cell" }
                },
                {
                    field: "IssDate",
                    title: "Issue Date",
                    attributes: { class: "d-none d-lg-table-cell name-field" },
                    headerAttributes: { class: "d-none d-lg-table-cell" }
                }
            ],
            dataBound: this.applicationsGridDataBound.bind(this),
            sortable: true,
            scrollable: true,
            resizable: true,
            htmlAttributes: { class: "kendo-Grid" }
        });


        const grid = $("#familyTreeInventionApplicationGrid").data("kendoGrid");
        grid.dataSource.read();
    }


    attachEventListeners() {
        d3.select("#familyTreeSvg g").selectAll("g.node").on("click", this.highlightNode.bind(this));

        $('#graphDirection').on('change', () => this.renderTreeInternal());
        $('#graphLayout').on('change', () => this.renderTreeInternal());

        $('[data-toggle="tooltip"]').tooltip();

        $('#FTViewToggleFull').on('click', () => {
            if (!this.fullscreen) {
                $('#InfoSideBar').hide();
                this.fullscreen = true;
            } else {
                $('#InfoSideBar').show();
                this.fullscreen = false;
            }
        });

        const isTD = this.isTD;
        function autoResize(svgElement, parentDiv) {
            const gElement = svgElement.querySelector("g");
            if (!gElement) return;

            const bbox = gElement.getBBox();
            const parentRect = parentDiv.getBoundingClientRect();
            const padding = 30;

            let availableHeight;
            let translateY;
            if (!isTD) {
                const headerElement = svgElement.querySelector("#svgHeader");
                const headerHeight = headerElement.getBBox().height;
                availableHeight = parentRect.height - headerHeight - padding;
                translateY = headerHeight + padding / 2 + (availableHeight / 2);
            } else {
                availableHeight = parentRect.height - padding;
                translateY = padding / 2 + (availableHeight / 2);
            }
           
            const computedScaleFactor = Math.min(
                parentRect.width / bbox.width,
                availableHeight / bbox.height
            );

            const scaleFactor = computedScaleFactor < 1 ? computedScaleFactor : 0.9;

            const xMid = bbox.x + bbox.width / 2;
            const yMid = bbox.y + bbox.height / 2;

            const translateX = parentRect.width / 2;

            gElement.setAttribute(
                "transform",
                `translate(${translateX}, ${translateY}) scale(${scaleFactor}) translate(${-xMid}, ${-yMid})`
            );
        }

        $(".exportPDF").click(() => {
            // console.log("Exporting PDF");
            const { jsPDF } = window.jspdf;

            cpiLoadingSpinner.show();

            this.clearInfo();
            this.centerGraph();

            try {
                if ($("#familyTreeSystem").val() === "patent")
                    this.getInventionApplicationGrid();

                const svgElement = document.querySelector("#familyTreeSvg");
                const foreignObjects = svgElement.querySelectorAll("foreignObject");
                const originalDisplay = [];

                foreignObjects.forEach((element, index) => {
                    originalDisplay[index] = element.style.display;
                    element.style.display = "none"; // Temporarily hide it
                });

                const parentDiv = document.querySelector("#d3-tree");
                autoResize(svgElement, parentDiv);

                const exportScale = 2 * (window.devicePixelRatio || 1);

                html2canvas(parentDiv, {
                    useCORS: true,
                    allowTaint: false,
                    scale: exportScale
                })
                    .then(async canvas => {
                        const imgData = canvas.toDataURL("image/jpeg", 1.0);
                        const margin = 40; // Margin around the image

                        const canvasWidthPt = canvas.width * 0.75;  //in points; todo
                        const canvasHeightPt = canvas.height * 0.75;

                        const pdf = new jsPDF({
                            orientation: canvasWidthPt > canvasHeightPt ? 'landscape' : 'portrait',
                            unit: 'pt',
                            format: [canvasWidthPt + 2 * margin, canvasHeightPt + 2 * margin]
                        });


                        const pdfWidth = pdf.internal.pageSize.getWidth();
                        const pdfHeight = pdf.internal.pageSize.getHeight();

                        const availableWidth = pdfWidth - 2 * margin;
                        const availableHeight = pdfHeight - 2 * margin;
                        // console.log(availableWidth, availableHeight);
                        // console.log(canvas.width, canvas.height);
                        const imgRatio = canvas.width / canvas.height;
                        const pdfRatio = availableWidth / availableHeight;
                        // console.log(imgRatio, pdfRatio);

                        let imgWidth, imgHeight;
                        if (imgRatio > pdfRatio) {
                            imgWidth = availableWidth;
                            imgHeight = (canvas.height * imgWidth) / canvas.width;
                        } else {
                            imgHeight = availableHeight;
                            imgWidth = (canvas.width * imgHeight) / canvas.height;
                        }

                        const imgX = (pdfWidth - imgWidth) / 2; // Centering the image
                        const imgY = (pdfHeight - imgHeight) / 2;

                        pdf.addImage(imgData, 'JPEG', imgX, imgY, imgWidth, imgHeight);

                        const logoUrl = `${window.location.origin}/images/site_report_logo.png`;
                        let logoWidth, logoHeight;


                        async function loadImageAndAddToPDF(logoUrl, pdf, margin) {
                            try {
                                const { width, height } = await loadImage(logoUrl);
                                logoWidth = width;
                                logoHeight = height;
                                pdf.addImage(logoUrl, 'PNG', margin, margin, logoWidth, logoHeight);
                            } catch (error) {
                                throw ('Error loading image:', error);
                            }
                        }

                        await loadImageAndAddToPDF(logoUrl, pdf, margin);

                        if ($("#familyTreeSystem").val() === "patent") {
                            pdf.addPage();

                            const headerElement = svgElement.querySelector("#svgHeader");
                            const svgBox = svgElement.getBBox();
                            const headerBox = headerElement.getBBox();

                            const cropRatio = headerBox.height / svgBox.height;

                            const cropHeight = Math.floor(canvas.height * cropRatio);

                            const croppedCanvas = document.createElement("canvas");
                            const ctx = croppedCanvas.getContext("2d");

                            croppedCanvas.width = canvas.width;
                            croppedCanvas.height = cropHeight;

                            // Crop only the top portion of the imgData
                            ctx.drawImage(
                                canvas,
                                0, 0,                      // Source X, Y
                                canvas.width, cropHeight,  // Source Width & Height
                                0, 0,                      // Destination X, Y
                                canvas.width, cropHeight   // Destination Width & Height
                            );

                            const croppedImgData = croppedCanvas.toDataURL("image/png");
                            pdf.addImage(croppedImgData, 'PNG', margin, margin, imgWidth, cropHeight * 0.75);

                            //console.log(logoWidth, logoHeight);
                            pdf.addImage(logoUrl, 'PNG', margin, margin, logoWidth, logoHeight);


                            const grid = $("#familyTreeInventionApplicationGrid").data("kendoGrid");
                            const dataSource = grid.dataSource.view();
                            const columns = grid.columns.map(col => col.title || col.field);

                            const headers = [columns];
                            const data = dataSource.map(item => grid.columns.map(col => item[col.field]));

                            const estimatedColWidth = availableWidth / columns.length;
                            const minFontSize = 14;
                            const maxFontSize = 30;
                            const calculatedFontSize = Math.max(
                                minFontSize,
                                Math.min(maxFontSize, (estimatedColWidth / 10)) // Adjust based on column width
                            );

                            pdf.autoTable({
                                head: headers,
                                body: data,
                                startY: Math.max(logoHeight, cropHeight) + margin + 10,
                                theme: "grid",
                                headStyles: {
                                    fillColor: [224, 224, 224],
                                    textColor: [0, 0, 0],
                                    fontStyle: "bold"
                                },
                                styles: {
                                    fontSize: calculatedFontSize,
                                    cellPadding: 5,
                                    overflow: 'linebreak', // Allow text to break across lines
                                    halign: 'center',
                                    // valign: 'middle',
                                },
                                tableWidth: 'auto', // Dynamically adjust table width
                            });
                        }

                        pdf.save("Family_Tree_View.pdf");
                    })
                    .catch(error => {
                        pageHelper.showErrors(`Error from generating report, please report this as a bug: ${error}`, 15);
                        console.error("html2canvas error:", error);
                    })
                    .finally(() => {
                        foreignObjects.forEach((element, index) => {
                            element.style.display = originalDisplay[index];
                        });
                        $("#familyTreeInventionApplicationGrid").empty();
                        const gElement = svgElement.querySelector("g");
                        if (gElement) gElement.removeAttribute("transform");

                        // this.highlightNode($("#familyTreeSelectedValue").val());
                        cpiLoadingSpinner.hide();
                    });
            } catch (error) {
                pageHelper.showErrors(`Error from generating report, please report this as a bug: ${error}`, 15);
                console.error("Please report this error:", error);
            }
        });


        $(".exportPPT").click(() => {
            cpiLoadingSpinner.show();

            this.clearInfo();
            this.centerGraph();

            if ($("#familyTreeSystem").val() === "patent")
                this.getInventionApplicationGrid();

            const svgElement = document.querySelector("#familyTreeSvg");
            const foreignObjects = svgElement.querySelectorAll("foreignObject");
            const originalDisplay = [];

            foreignObjects.forEach((element, index) => {
                originalDisplay[index] = element.style.display;
                element.style.display = "none"; // Temporarily hide it
            });

            const parentDiv = document.querySelector("#d3-tree");
            autoResize(svgElement, parentDiv);

            const exportScale = 2 * (window.devicePixelRatio || 1);

            html2canvas(parentDiv, {
                useCORS: true,
                allowTaint: false,
                scale: exportScale
            })
                .then(async (canvas) => {
                    const imgData = canvas.toDataURL("image/jpeg", 1.0);
                    const pptx = new PptxGenJS();

                    const pptWidth = 10; // Default width in inches
                    const pptHeight = 5.625; // Default height in inches
                    pptx.defineLayout({ name: "MSFT PPT", width: pptWidth, height: pptHeight });

                    const slide = pptx.addSlide({ layout: "MSFT PPT" });

                    const imgRatio = canvas.width / canvas.height;
                    let imgWidth = pptWidth - 1; // Slight margin from edges
                    let imgHeight = imgWidth / imgRatio;

                    slide.addImage({
                        data: imgData,
                        x: (pptWidth - imgWidth) / 2, // Centering the image
                        y: (pptHeight - imgHeight) / 2,
                        w: imgWidth,
                        h: imgHeight
                    });


                    const logoUrl = `${window.location.origin}/images/site_report_logo.png`;
                    let logoWidth, logoHeight;
                    const margin = 0.2;

                    const logoBase64 = await fetch(logoUrl)
                        .then(res => res.blob())
                        .then(blob => new Promise((resolve) => {
                            const reader = new FileReader();
                            reader.onloadend = () => resolve(reader.result.split(",")[1]); // Get base64 without prefix
                            reader.readAsDataURL(blob);
                        })); // Adjusted for pptx units
                    async function loadImageAndAddToSlide(logoUrl, slide, margin) {
                        try {
                            const { width, height } = await loadImage(logoUrl);
                            logoWidth = width / 96 * 0.75;
                            logoHeight = height / 96 * 0.75;
                            //console.log(logoWidth, logoHeight);
                            slide.addImage({
                                data: `data:image/png;base64,${logoBase64}`,
                                x: margin,
                                y: margin,
                                w: logoWidth,
                                h: logoHeight
                            });
                        } catch (error) {
                            throw ('Error loading image:', error);
                        }
                    }

                    await loadImageAndAddToSlide(logoUrl, slide, margin);

                    if ($("#familyTreeSystem").val() === "patent") {

                        const grid = $("#familyTreeInventionApplicationGrid").data("kendoGrid");
                        const dataSource = grid.dataSource.view();
                        const columns = grid.columns.map(col => col.title || col.field);

                        const headers = columns.map(title => ({ text: title, options: { bold: true, fill: 'E0E0E0' } }));
                        const data = dataSource.map(item => grid.columns.map(col => ({ text: item[col.field] || "", options: {} })));

                        const totalCols = columns.length;
                        const estimatedColWidth = (pptWidth - 1) / totalCols;

                        // Need a better way to calculate font
                        //const calculatedFontSize = Math.max(
                        //    14, // minFontSize
                        //    Math.min(20, (estimatedColWidth / 10)) // maxFontSize; adjust based on column width
                        //);

                        const calculatedFontSize = 10;
                        const maxRowsPerSlide = 12;

                        function addSlideWithTable(rows) {
                            const slide = pptx.addSlide({ layout: "MSFT PPT" });

                            // Add logo
                            slide.addImage({
                                data: `data:image/png;base64,${logoBase64}`,
                                x: margin,
                                y: margin,
                                w: logoWidth,
                                h: logoHeight
                            });

                            // Add table
                            slide.addTable(
                                [headers, ...rows],
                                {
                                    x: 0.5,
                                    y: Math.max(1, margin + logoHeight),
                                    w: pptWidth - 1,
                                    colW: estimatedColWidth,
                                    fontSize: calculatedFontSize,
                                    color: "000000",
                                    border: { pt: "1", color: "C0C0C0" },
                                    align: "center",
                                }
                            );
                        }

                        // Split data into multiple slides
                        for (let i = 0; i < data.length; i += maxRowsPerSlide) {
                            const chunk = data.slice(i, i + maxRowsPerSlide);
                            addSlideWithTable(chunk);
                        }
                    }

                    pptx.writeFile({ fileName: "Family_Tree_View.pptx" });
                })
                .catch((error) => {
                    pageHelper.showErrors(error);
                    console.error("html2canvas error:", error);
                })
                .finally(() => {
                    foreignObjects.forEach((element, index) => {
                        element.style.display = originalDisplay[index];
                    });

                    $("#familyTreeInventionApplicationGrid").empty();
                    const gElement = svgElement.querySelector("g");
                    if (gElement) gElement.removeAttribute("transform");

                    cpiLoadingSpinner.hide();
                });
        });

    }


    highlightNode(nodeId) {
        this.clearInfo();
        if (nodeId !== $("#familyTreeSelectedValue").val()) {
            const node = g.node(nodeId);
            this.displayInfo(node);
            this.centerAroundNode(node);
        } else {
            $("#familyTreeSelectedValue").val("");
        }
    }


    clearInfo() {
        $("#info").empty();
        g.nodes().forEach(id => {
            $(`#${id}`).removeClass('childPat parentPat selectedNode');
        });

        // $("#familyTreeSelectedValue").val("");

        g.edges().forEach(edgeId => {
            const edge = g.edge(edgeId);
            if (edge && edge.elem) {
                $(edge.elem).removeClass('childEdge parentEdge');
            }
        });
    }


    displayInfo(node) {
        let isTradeSecret = this.treeJson["Area"] === 2;
        let info = document.createElement("div");
        info.classList.add("infoDetails");

        info.appendChild(createInfoHeader(node));

        if (isTradeSecret) {
            const div = document.createElement("div");
            div.classList.add("infoDetailsContent");

            const span = document.createElement("span");
            span.classList.add("dymo-alert");
            span.textContent = "Trade Secret";
            info.appendChild(div.appendChild(span));
        }

        if (node.Type !== "F") {
            info.appendChild(createInfo((node.Type !== "T" ? "Title" : "Trademark"), (node["Title"] === null ? "" : node["Title"])));
            this.nodeDisplayOptions.forEach(option => {
                if (!option.Include) return;
                if (option.PropertyName !== "Client" && node["Type"] !== "C" && node["Type"] !== "T")
                    return;

                const value = node[option.PropertyName] || "";
                info.appendChild(createInfo(option.Label, value));
            });
        }

        const parentHeight = $("#InfoSideBar").parent().height();

        $("#InfoSideBar").css({
            height: parentHeight + "px",
            overflowY: "auto"
        });

        $("#info").append(info);

        $(node.elem).addClass('selectedNode');
        $("#familyTreeSelectedValue").val(node.Id);

        this.highlightForwards(node.Id);
        this.highlightBackwards(node.Id);
    }


    highlightForwards(nodeId) {
        const visited = new Set();
        const stack = [nodeId];

        while (stack.length > 0) {
            const currentId = stack.pop();
            visited.add(currentId);

            //if (currentId !== startId) {
            //    $(document.getElementById(currentId)).addClass('childNode');
            //}

            const children = this.parentToChildren.get(currentId);
            if (!children) continue;

            for (const childId of children) {
                const edge = g.edge(currentId, childId);
                if (edge && edge.elem) {
                    $(edge.elem).addClass('childEdge');
                }

                if (!visited.has(childId)) {
                    stack.push(childId);
                }
            }
        }
    }


    highlightBackwards(nodeId) {
        const visited = new Set();
        const stack = [nodeId];

        while (stack.length) {
            const currentId = stack.pop();
            visited.add(currentId);

            //if (nodeName !== anId) {
            //    $(`#${nodeName}`).addClass('parentNode');
            //}

            const parents = this.childToParents.get(currentId);
            if (!parents) continue;

            for (const parentId of parents) {
                const edge = g.edge(parentId, currentId);
                if (!edge) continue;

                $(edge.elem).addClass('parentEdge');

                if (!visited.has(parentId)) {
                    stack.push(parentId);
                }
            }
        }
    }



    attachViewHeader() {
        let svg = d3.select("#familyTreeSvg");
        let header = this.treeJson['Header'];

        const viewTitle = [
            { bold: `Family Tree for ${header.LabelCaseNumber}: `, normal: ` ${header.CaseNumber}` },
            { bold: `${header.LabelTitle}: `, normal: ` ${formatList(header.Title)}` },
            { bold: `${header.LabelClient}: `, normal: ` ${formatList(header.Client)}` }
        ];

        $("#svgHeader").remove();

        let textElement = svg.append("text")
            .attr("id", "svgHeader")
            .attr("x", "50%")
            .attr("y", 15)
            .attr("text-anchor", "middle")
            .attr("font-size", "16px")
            .attr("fill", "rgb(47, 72, 88)");

        //let viewBoxWidth = svg.attr("viewBox")?.split(" ")[2]; 
        let maxWidth = $("#d3-tree").width() / 3;

        function wrapText(text, width) {
            let words = text.split(/\s+/);
            let lines = [];
            let currentLine = words.shift();

            words.forEach(word => {
                if ((currentLine + " " + word).length > width / 6) { // Rough character width calculation
                    lines.push(currentLine);
                    currentLine = word;
                } else {
                    currentLine += " " + word;
                }
            });

            lines.push(currentLine);
            return lines;
        }


        viewTitle.forEach((line, i) => {
            let wrappedLines = wrapText(line.normal.trim(), maxWidth);
            let tspan = textElement.append("tspan")
                .attr("x", "50%")
                .attr("dy", i === 0 ? "0em" : `1.2em`);

            tspan.append("tspan")
                .attr("font-weight", "bold")
                .text(line.bold);

            wrappedLines.forEach((wrappedLine, j) => {
                if (j === 0) {
                    tspan.append("tspan")
                        .text(j === 0 ? wrappedLine : " " + wrappedLine);
                } else {
                    textElement.append("tspan")
                        .attr("x", "50%")
                        .attr("dy", `1.2em`)
                        .text(" " + wrappedLine);
                }
            });

        });
    }


    attachViewStatistics() {
        let svg = d3.select("#familyTreeSvg");
        let stats = this.treeJson['Stats'];
        var viewStats;
        if ($("#familyTreeSystem").val() === "patent") {
            let priority = ["Priority: "]
            for (let i = 0; i < stats.PriDate.length; i++) {
                const priorityInfo = (stats.PriCountry[i] || "") + " " + (stats.PriNumber[i] || "") + " " + (stats.PriDate[i] ? formatDate(stats.PriDate[i]) : "");
                priority[priority.length - 1] += priorityInfo;
                if (i < stats.PriDate.length - 1) {
                    if (priorityInfo.trim() !== "") {
                        priority.push("; ");
                    } else {
                        priority[priority.length - 1] += ", ";
                    }
                }
            }

            let appStats = [
                `Number of Active Applications: ${stats.ActiveCount}`,
                `Number of Inactive Applications: ${stats.InactiveCount}`,
                `Number of Expired Patents: ${stats.ExpiredPatents}`,
                `Number of Validated Applications: ${stats.ValidatedApps}`
            ]

            viewStats = priority.concat(appStats);
        } else {
            viewStats = [
                `Mark Type: ${stats.MarkType || ""}`,
                `Classes: ${stats.Classes || ""}`,
                `Number of Active Trademarks: ${stats.ActiveCount}`,
                `Number of Inactive Applications: ${stats.InactiveCount}`,
                `Madrid Protocol: ${stats.MadridProtocol ? "Yes" : "No"}`,
                `European Union (Community): ${stats.EuropeanUnion ? "Yes" : "No"}`,
            ]
        }


        let bbox = svg.node().getBoundingClientRect();
        let svgHeight = bbox.height;

        $("#svgFooter").remove();

        let textElement = svg.append("text")
            .attr("id", "svgFooter")
            .attr("x", 10)
            .attr("y", svgHeight - viewStats.length * 16)
            .attr("text-anchor", "start")
            .attr("font-size", "14px")
            .attr("fill", "rgb(85, 113, 124)");

        viewStats.forEach((line, i, arr) => {
            textElement.append("tspan")
                .attr("x", 10)
                .attr("dy", i === 0 ? "0em" : "1.2em")
                .text(line);
        });
    }


    attachViewLegend() {
        const svg = d3.select("svg");
        let bbox = svg.node().getBoundingClientRect();
        let legendY = bbox.height - 60;
        const legendGroup = svg.append("g")
            .attr("id", "legend")
            .attr("transform", `translate(20, ${legendY})`);

        legendGroup.append("rect")
            .attr("width", 220)
            .attr("height", 50)
            .attr("fill", "white")
            .attr("stroke", "black")
            .attr("stroke-width", 2);

        legendGroup.append("line")
            .attr("x1", 10)
            .attr("y1", 15)
            .attr("x2", 100)
            .attr("y2", 15)
            .attr("stroke", "black")
            .attr("marker-end", "url(#arrowhead)");

        legendGroup.append("text")
            .attr("x", 110)
            .attr("y", 19)
            .attr("font-size", "12px")
            .attr("font-family", "serif")
            .text("Continuation");

        legendGroup.append("line")
            .attr("x1", 10)
            .attr("y1", 35)
            .attr("x2", 100)
            .attr("y2", 35)
            .attr("stroke", "black")
            .attr("stroke-dasharray", "4,2")
            .attr("marker-end", "url(#arrowhead)");

        legendGroup.append("text")
            .attr("x", 110)
            .attr("y", 39)
            .attr("font-size", "12px")
            .attr("font-family", "serif")
            .text("Continuation-in-part");
    }


    renderTreeInternal() {
        this.parentToChildren = new Map();
        this.childToParents = new Map();
        let treeJson = this.treeJson;
        let treeNodes = treeJson['Nodes'];
        let relationships = treeJson['Edges'];

        g = new dagreD3.graphlib.Graph()
            .setGraph({
                ranksep: 150,
                rankdir: $('#graphDirection').val() || "LR",
                ranker: $('#graphLayout').val() || "network-simplex",
            });

        // set nodes
        for (let i = 0; i < treeNodes.length; i++) {
            let node = treeNodes[i];
            node.label = "";
            node.id = String(node.Id);

            if (!this.isTD) {
                node.shape = "treeNode";
            } else {
                // Appl.
                node.shape = "TDTreeNode";
                let nodePatentCopy = structuredClone({ ...node });
                let nodePTACopy = structuredClone({ ...node });

                node.Text = node.Text + " Appl."
                node.TDProperties = ["Text", "FilDate"];
                if (!node.FilDate)
                    node.FilDate = "No FilDate date";

                node.shapeGap = 10;
                if (i > 0) {
                    // Patent
                    nodePatentCopy.id = nodePatentCopy.id + "Patent"
                    nodePatentCopy.Text = nodePatentCopy.Text + " Patent"
                    nodePatentCopy.TDProperties = ["Text", "IssDate"];
                    if (!nodePatentCopy.IssDate)
                        nodePatentCopy.IssDate = "No IssDate date";

                    g.setNode(nodePatentCopy.id, nodePatentCopy);
                    g.setEdge(node.id, nodePatentCopy.id, { arrowhead: "undirected" });


                    // PTA
                    nodePTACopy.TDProperties = ["Text"];
                    nodePTACopy.id = nodePTACopy.id + "PTA"
                    if (nodePTACopy.PatentTermAdj) {
                        nodePTACopy.Text = nodePTACopy.PatentTermAdj + " Day PTA";
                    } else
                        nodePTACopy.Text = "No PTA";

                    g.setNode(nodePTACopy.id, nodePTACopy);
                    g.setEdge(nodePatentCopy.id, nodePTACopy.id, { arrowhead: "undirected" });
                }
            }

            g.setNode(node.id, node);
        }

        // set edges
        let edgeNodes = new Set();
        for (let i = 0, len = relationships.length; i < len; i++) {
            let edge = relationships[i];
            let start = edge.Start;
            let end = edge.End;

            let currentForwardEdges = this.parentToChildren.get(start);
            if (currentForwardEdges === undefined) {
                currentForwardEdges = [];
                this.parentToChildren.set(start, currentForwardEdges);
            }
            currentForwardEdges.push(end);

            let currentBackwardEdges = this.childToParents.get(end);
            if (currentBackwardEdges === undefined) {
                currentBackwardEdges = [];
                this.childToParents.set(end, currentBackwardEdges);
            }
            currentBackwardEdges.push(start);

            if (!edgeNodes.has(end)) {
                edgeNodes.add(end);
            } else {
                edgeNodes.delete(end);
            }

            if (!this.isTD) {
                let svgLabel = createEdgeLabel(edge.Label);
                g.setEdge(start, end, { labelType: "svg", label: svgLabel, curve: d3.curveBasis });
            } else {
                if (edge.Label === "CIP")
                    g.setEdge(start, end, { style: "stroke-dasharray: 7, 2;"});
                else
                    g.setEdge(start, end, { style: ""});
            }
        }

        let svg = d3.select("#familyTreeSvg");
        if (svg.select("g").empty()) {
            svg.append("g");
        }
        let inner = svg.select("g");

        zoom = d3.zoom()
            .scaleExtent([0.05, 10])
            .on("zoom", function () {
                if (isFinite(d3.event.transform.x) && isFinite(d3.event.transform.y)) {
                    inner.attr("transform", d3.event.transform);
                }
            });
        svg.call(zoom);

        this.render(inner, g);
        this.clearInfo();

        if (!this.isTD) {
            this.attachViewHeader();
            this.attachViewStatistics();
        } else {
            this.attachViewLegend();
        }

        this.centerGraph();

        let selectedNode = g.node($("#familyTreeParamValue").val());
        if (selectedNode !== undefined) {
            this.displayInfo(selectedNode);
        }

        if ($("#familyTreeParamValue").val() != "") {
            $("#" + $("#familyTreeParamValue").val()).addClass("mainNode");
        }
    }


    async reRender(toFetchTreeData, toFetchDisplayOptions) {
        if (toFetchTreeData) {
            this.treeJson = await this.fetchTreeData();
            d3.select("#familyTreeSvg").empty();
            this.renderTreeInternal();
        }
        if (toFetchDisplayOptions) {
            let { properties, _ } = await this.fetchDisplayOptions();
            this.nodeDisplayOptions = properties;
            let selectedNode = g.node($("#familyTreeSelectedValue").val());
            if (selectedNode !== undefined) {
                $("#info").empty();
                this.displayInfo(selectedNode);
            }
        }
    }


    async fetchTreeData() {
        cpiLoadingSpinner.show();
        return $.ajax({
            url: $("#d3-tree").data('data-url'),
            type: "GET",
            dataType: "json",
            data: this.fetchTreeArgument,
            success: (response) => {
                //console.log(response);  // debug purpose
            },
            error: (xhr, status, error) => {
                console.error(error);
                pageHelper.showErrors(`Error rendering family tree, please report this as a bug.`);
            },
            complete: () => {
                cpiLoadingSpinner.hide();
            }
        });
    }


    async fetchDisplayOptions() {
        cpiLoadingSpinner.show();
        return $.ajax({
            url: $("#d3-tree").data('settings-url'),
            type: "GET",
            dataType: "json",
            success: (response) => {
                const properties = response.properties;
                let checkboxHtml = "";

                $.each(properties, function (index, item) {
                    const checkedAttr = item.Include ? 'checked' : '';
                    checkboxHtml += `
                        <div class="col-md-4">
                            <input type="checkbox" class="k-checkbox" id="${item.PropertyName}" name="${item.PropertyName}" ${checkedAttr}>
                            <label class="k-checkbox-label" for="${item.PropertyName}">
                                ${item.Label}
                            </label>
                        </div>`;
                });

                const modal = $("#familyTreeDataSettingDialog");
                if (modal.length > 0) {
                    const settingsContainer = modal.find('.settings-fields');
                    settingsContainer.empty().append(checkboxHtml);
                    $("#tempSettingsContainer, #tempIsSettingDefault").remove();
                    modal.find('#DefaultSetting').prop('checked', response.isSettingDefault);
                } else {
                    let $tempSettingsContainer = $("#tempSettingsContainer");
                    if ($tempSettingsContainer.length > 0) {
                        $tempSettingsContainer.empty();
                        $tempSettingsContainer.append(checkboxHtml);
                    } else {
                        $tempSettingsContainer = $(`<div id="tempSettingsContainer" style="display:none;"></div>`).append(checkboxHtml);
                        $("body").append($tempSettingsContainer);
                    }
                    let $tempIsSettingDefault = $("#tempIsSettingDefault");
                    if ($tempIsSettingDefault.length > 0) {
                        $tempIsSettingDefault.val(response.isSettingDefault);
                    } else {
                        $tempIsSettingDefault = $(`<input type="hidden" id="tempIsSettingDefault" value="${response.isSettingDefault}" />`);
                        $("body").append($tempIsSettingDefault);
                    }
                }
            },
            error: function (xhr, status, error) {
                console.error(error);
                pageHelper.showErrors(`Error rendering family tree, please report this as a bug.`);
            },
            complete: function () {
                cpiLoadingSpinner.hide();
            }
        });
    }
}


